import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { getCallDetails, getCallRecordingUrl } from '../services/api';
import './CallDetailPage.css';

const CallDetailPage = ({ callId, onClose, isRbAdmin = false, isDyopathAdmin = false, isDyopathManager = false }) => {
  const { accounts } = useMsal();
  const userEmail = accounts[0]?.username || '';
  const [callData, setCallData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCallDetails = async () => {
    try {
      setLoading(true);
      const data = await getCallDetails(callId);
      setCallData(data.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching call details:', err);
      setError('Failed to fetch call details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCallDetails();
  }, [callId]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  if (loading && !callData) {
    return (
      <div className="call-detail-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading call details...</p>
        </div>
      </div>
    );
  }

  if (error && !callData) {
    return (
      <div className="call-detail-page">
        <div className="error-container">
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={onClose}>Back</button>
        </div>
      </div>
    );
  }

  const totalDuration = callData?.duration || 0;
  const handleDuration = callData?.handle_duration || 0;
  const holdDuration = callData?.hold_duration || 0;
  const waitDuration = callData?.wait_duration || 0;

  // Determine if current user can download/listen to this call recording.
  // Org is decided server-side (RBDCAI-1070); fall back to email domain only if absent.
  const agentEmail = callData?.agent?.email?.toLowerCase() || '';
  const agentOrg = callData?.agent?.org
    || ((agentEmail.split('@')[1] || '') === 'dyopath.com' ? 'dyopath' : 'redbull');
  const canDownloadRecording = isRbAdmin || ((isDyopathAdmin || isDyopathManager) && agentOrg === 'dyopath');

  return (
    <div className="call-detail-page">
      {/* Header */}
      <div className="call-header">
        <button className="back-btn" onClick={onClose}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        
        <div className="call-header-info">
          <h1>Call Details</h1>
          <div className="call-id-badge">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            {callId}
          </div>
          <div className={`call-status-badge ${callData?.status}`}>
            {callData?.status || 'Unknown'}
          </div>
        </div>
      </div>

      {/* Call Overview Cards */}
      <div className="call-metrics-grid">
        <div className="metric-card-large">
          <div className="metric-icon-large duration">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="metric-info-large">
            <div className="metric-value-large">{formatDuration(totalDuration)}</div>
            <div className="metric-label-large">Total Duration</div>
          </div>
        </div>

        <div className="metric-card-large">
          <div className="metric-icon-large phone">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </div>
          <div className="metric-info-large">
            <div className="metric-value-large">{callData?.consumer_phone_number || 'N/A'}</div>
            <div className="metric-label-large">Caller Number</div>
          </div>
        </div>

        <div className="metric-card-large">
          <div className="metric-icon-large direction">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
              {callData?.direction === 'inbound' ? (
                <path d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              ) : (
                <path d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
              )}
            </svg>
          </div>
          <div className="metric-info-large">
            <div className="metric-value-large" style={{ textTransform: 'capitalize' }}>
              {callData?.direction || 'Unknown'}
            </div>
            <div className="metric-label-large">Direction</div>
          </div>
        </div>

        <div className="metric-card-large">
          <div className="metric-icon-large agent">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
              <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <div className="metric-info-large">
            <div className="metric-value-large">{callData?.agent?.name || 'N/A'}</div>
            <div className="metric-label-large">Handled By</div>
          </div>
        </div>
      </div>

      {/* Time Breakdown Chart */}
      <div className="call-chart-section">
        <div className="chart-card-call">
          <h3 className="chart-heading">Time Breakdown</h3>
          <div className="time-breakdown-chart">
            {(() => {
              // Calculate max value for relative sizing and get total
              const maxTime = Math.max(waitDuration, handleDuration, holdDuration, 1);
              const totalTime = waitDuration + handleDuration + holdDuration;
              
              return (
                <>
                  <div className="time-segment-row">
                    <div className="time-segment-label">
                      <span className="time-dot wait"></span>
                      Wait Time
                    </div>
                    <div className="time-segment-bar-container">
                      <div 
                        className="time-segment-bar wait"
                        style={{ width: `${(waitDuration / maxTime) * 100}%` }}
                      >
                        <span className="time-segment-text">{formatDuration(waitDuration)}</span>
                      </div>
                    </div>
                    <div className="time-segment-value">
                      {totalTime > 0 ? Math.round((waitDuration / totalTime) * 100) : 0}%
                    </div>
                  </div>

                  <div className="time-segment-row">
                    <div className="time-segment-label">
                      <span className="time-dot handle"></span>
                      Handle Time
                    </div>
                    <div className="time-segment-bar-container">
                      <div 
                        className="time-segment-bar handle"
                        style={{ width: `${(handleDuration / maxTime) * 100}%` }}
                      >
                        <span className="time-segment-text">{formatDuration(handleDuration)}</span>
                      </div>
                    </div>
                    <div className="time-segment-value">
                      {totalTime > 0 ? Math.round((handleDuration / totalTime) * 100) : 0}%
                    </div>
                  </div>

                  <div className="time-segment-row">
                    <div className="time-segment-label">
                      <span className="time-dot hold"></span>
                      Hold Time
                    </div>
                    <div className="time-segment-bar-container">
                      <div 
                        className="time-segment-bar hold"
                        style={{ width: `${(holdDuration / maxTime) * 100}%` }}
                      >
                        <span className="time-segment-text">{formatDuration(holdDuration)}</span>
                      </div>
                    </div>
                    <div className="time-segment-value">
                      {totalTime > 0 ? Math.round((holdDuration / totalTime) * 100) : 0}%
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>

        {/* Call Timeline */}
        <div className="chart-card-call">
          <h3 className="chart-heading">Call Timeline</h3>
          <div className="call-timeline">
            <div className="timeline-item">
              <div className="timeline-icon started">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="timeline-content">
                <div className="timeline-title">Call Started</div>
                <div className="timeline-time">{formatDateTime(callData?.started_at)}</div>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-icon queue">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <div className="timeline-content">
                <div className="timeline-title">In Queue: {callData?.queue?.name || 'General'}</div>
                <div className="timeline-time">Wait: {formatDuration(waitDuration)}</div>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-icon answered">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div className="timeline-content">
                <div className="timeline-title">Answered by {callData?.agent?.name || 'Agent'}</div>
                <div className="timeline-time">Handle: {formatDuration(handleDuration)}</div>
              </div>
            </div>

            {holdDuration > 0 && (
              <div className="timeline-item">
                <div className="timeline-icon hold">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="timeline-content">
                  <div className="timeline-title">On Hold</div>
                  <div className="timeline-time">{formatDuration(holdDuration)}</div>
                </div>
              </div>
            )}

            <div className="timeline-item">
              <div className="timeline-icon ended">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="timeline-content">
                <div className="timeline-title">Call Ended</div>
                <div className="timeline-time">{formatDateTime(callData?.ended_at)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Call Recording Player - Admins only */}
      {canDownloadRecording && callData?.recording_url && callData.recording_url.trim() !== '' && (() => {
        const proxyUrl = getCallRecordingUrl(callId, userEmail);
        return (
        <div className="call-recording-section">
          <h3 className="section-heading">Call Recording</h3>
          <div className="recording-player-container">
            <div className="recording-player-header">
              <div className="recording-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              </div>
              <div className="recording-info">
                <div className="recording-title">Call Recording Available</div>
                <div className="recording-subtitle">Duration: {formatDuration(totalDuration)}</div>
              </div>
            </div>

            <audio
              controls
              className="audio-player"
              preload="metadata"
            >
              <source src={proxyUrl} type="audio/mpeg" />
              Your browser does not support the audio element.
            </audio>

            <div className="recording-controls">
              <a
                href={proxyUrl}
                className="download-recording-btn"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
                </svg>
                Download Recording
              </a>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Disposition and Details */}
      {callData?.dispositions && callData.dispositions.length > 0 && (
        <div className="call-details-section">
          <h3 className="section-heading">Disposition Codes</h3>
          <div className="disposition-grid">
            {callData.dispositions.map((disp, idx) => (
              <div key={idx} className="disposition-card">
                <div className="disposition-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="disposition-info">
                  <div className="disposition-code">{disp.code}</div>
                  {disp.agent_id && <div className="disposition-agent">Agent ID: {disp.agent_id}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Linked Ticket */}
      {callData?.out_ticket_url && (
        <div className="call-ticket-section">
          <h3 className="section-heading">Linked Ticket</h3>
          <div className="ticket-card">
            <div className="ticket-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
              </svg>
            </div>
            <div className="ticket-info">
              <div className="ticket-id">ServiceNow Incident</div>
              <div className="ticket-source">Ref: {callData.out_ticket_id ? callData.out_ticket_id.substring(0, 8) + '...' : 'N/A'}</div>
            </div>
            <a
              href={callData.out_ticket_url}
              target="_blank"
              rel="noopener noreferrer"
              className="ticket-link-btn"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Open Ticket
            </a>
          </div>
        </div>
      )}

      {/* Call Information Table */}
      <div className="call-info-section">
        <h3 className="section-heading">Call Information</h3>
        <div className="info-table">
          <div className="info-row">
            <span className="info-label">Call ID:</span>
            <span className="info-value">{callId}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Started:</span>
            <span className="info-value">{formatDateTime(callData?.started_at)}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Ended:</span>
            <span className="info-value">{formatDateTime(callData?.ended_at)}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Total Duration:</span>
            <span className="info-value">{formatDuration(totalDuration)}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Wait Time:</span>
            <span className="info-value">{formatDuration(waitDuration)}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Handle Time:</span>
            <span className="info-value">{formatDuration(handleDuration)}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Hold Time:</span>
            <span className="info-value">{formatDuration(holdDuration)}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Status:</span>
            <span className="info-value" style={{ textTransform: 'capitalize' }}>{callData?.status || 'N/A'}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Direction:</span>
            <span className="info-value" style={{ textTransform: 'capitalize' }}>{callData?.direction || 'N/A'}</span>
          </div>
          {callData?.queue && (
            <div className="info-row">
              <span className="info-label">Queue:</span>
              <span className="info-value">{callData.queue.name}</span>
            </div>
          )}
          {callData?.agent && (
            <>
              <div className="info-row">
                <span className="info-label">Agent:</span>
                <span className="info-value">{callData.agent.name}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Agent Email:</span>
                <span className="info-value">{callData.agent.email}</span>
              </div>
            </>
          )}
          {callData?.out_ticket_url && (
            <div className="info-row">
              <span className="info-label">Ticket:</span>
              <span className="info-value">
                <a href={callData.out_ticket_url} target="_blank" rel="noopener noreferrer" className="ticket-inline-link">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{verticalAlign: 'middle', marginRight: '4px'}}>
                    <path d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  View Ticket in ServiceNow
                </a>
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallDetailPage;

